import { getIcon } from './getIcon';
import { Columns } from './Columns';
import { CardTag } from './CardTag';
import { CardIcon } from './CardIcon';
import { stripHTML } from './stripHTML';
import { logEvent } from '@/utils/analytics';
import { colors, Icon } from '@zopauk/react-components';
import { SanitizeColumnData } from './SanitizeColumnData';
import type { Card as CardType } from '@/utils/models/cards';
import { ProductLabelV2, ProductLabel } from './ProductLabel';
import { ConditionalLinkWrapper } from './ConditionalLinkWrapper';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import Events from '@/utils/amplitudeEvents.json' with { type: 'json' };

function sanitizeCard(card: CardType) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { secondaryData, primaryData, ...rest } = card;
  return {
    ...rest,
  };
}

export const Card = (card: CardType) => {
  const icon = getIcon(card.template);

  return (
    <ConditionalLinkWrapper
      onClick={() => {
        logEvent(Events.CARD_CLICK, { card: sanitizeCard(card) });
        if (card.version === '2' && card.event) {
          logEvent(card.event, { card: sanitizeCard(card) });
        }
      }}
      className={`has-icon no-decoration ${card.template} ${card.category}`}
      condition={card.actionType === 'redirect'}
      href={card.href}
    >
      <Columns>
        <CardTitleColumn template={card.template}>
          {card?.version === '2' ? (
            <ProductLabelV2>{card.productLabel ? card.productLabel : card.label.text || 'Zopa Product'}</ProductLabelV2>
          ) : (
            <ProductLabel card={card} />
          )}
          <span>{card.title ? stripHTML(card.title) : null}</span>
          {card.tag ? <CardTag>{card.tag.text}</CardTag> : null}
        </CardTitleColumn>
        {icon ? (
          <CardIconColumn template={card.template}>
            <CardIcon icon={icon} />
          </CardIconColumn>
        ) : null}
        {card.primaryData ? (
          <div className="column primary-column" data-amp-mask>
            {SanitizeColumnData(card.primaryData)}
          </div>
        ) : null}
        {card.secondaryData ? (
          <div className="column secondary-column" data-amp-mask>
            {SanitizeColumnData(card.secondaryData)}
          </div>
        ) : null}
      </Columns>
      <Icon
        color={card.category === 'discovery' ? colors.actionPlain : colors.brandDecorative}
        variant={faChevronRight}
        className="chevron"
      />
    </ConditionalLinkWrapper>
  );
};

interface WithCardTemplate {
  template: CardType['template'];
}

export function CardTitleColumn({ template, children }: React.PropsWithChildren<WithCardTemplate>) {
  const className = template === 'discoveryWide' ? 'max-w-500' : 'max-w-300';
  return <div className={`column title-column ${className}`}>{children}</div>;
}

export function CardTitleWideColumn({ children }: React.PropsWithChildren<WithCardTemplate>) {
  return <div className="column wide-title-column">{children}</div>;
}

export function CardIconColumn({ children }: React.PropsWithChildren<WithCardTemplate>) {
  return <div className="column icon-column">{children}</div>;
}
